import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Loading from '../loading/Loading'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import Button from '@mui/material/Button'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import Grid from '@mui/material/Grid'

import { AdminManage } from '../styles'

import { PAGES } from '../navigation/routes'

import { setErrorNotification } from '../../reducers/notificationReducer'
import { initializeUsers, updateUser } from '../../reducers/usersReducer'
import userService from '../../services/user'

const buildUsersQuery = (search = '') => {
    const params = ['staff=false']

    if (search.trim()) {
        params.push(`search=${encodeURIComponent(search.trim())}`)
    }

    return params.join('&')
}

const ManageAdmin = () => {
    const [loadingAdmins, setLoadingAdmins] = useState(true)
    const [filter, setFilter] = useState('')
    const [searchDraft, setSearchDraft] = useState('')
    const [admins, setAdmins] = useState([])

    const userData = useSelector(state => state.users)
    const searchUsers = userData?.results || []
    const totalPages = userData?.total_pages || 1
    const loadingUsers = userData?.loading || false
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [page, setPage] = useState(1)

    const loadAdmins = useCallback(async () => {
        setLoadingAdmins(true)
        try {
            const data = await userService.getAdmins()
            setAdmins(data)
        } catch {
            setAdmins([])
            dispatch(setErrorNotification('Error retrieving admins'))
        } finally {
            setLoadingAdmins(false)
        }
    }, [dispatch])

    const loadUsers = useCallback((pageToLoad = page) => {
        dispatch(initializeUsers(buildUsersQuery(filter), pageToLoad))
    }, [dispatch, filter, page])

    useEffect(() => {
        // RequireStaff (App.js) guarantees this component only mounts for a
        // loaded, staff user — this check just guards against `user` being
        // momentarily stale on the very first effect run.
        if (user?.staff) {
            loadAdmins()
        }
    }, [user, loadAdmins])

    useEffect(() => {
        if (user?.staff) {
            loadUsers(page)
        }
    }, [user, loadUsers, page])

    // Debounce the search-driven filter: keep it in local draft state and only
    // lift it into `filter` (which `loadUsers` and its effect key off of) once
    // typing pauses for ~400ms. Mirrors ListFilters in ManageAutomation.js.
    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchDraft !== filter) {
                setFilter(searchDraft)
                setPage(1)
            }
        }, 400)
        return () => clearTimeout(handle)
    }, [searchDraft, filter])

    const refreshUserLists = useCallback(async (pageToLoad = page) => {
        await loadAdmins()
        dispatch(initializeUsers(buildUsersQuery(filter), pageToLoad))
    }, [dispatch, filter, loadAdmins, page])

    const handlePromote = async (u) => {
        if (user.admin) {
            if (window.confirm('Are you sure you want to promote this user?')) {
                const data = {
                    ...u,
                    staff: true
                }

                await dispatch(updateUser(data))
                await refreshUserLists(page)
            }
        } else {
            dispatch(setErrorNotification('You do not have permission to promote users'))
        }
    }

    const handleDemote = async (u) => {
        if (user.admin) {
            if (window.confirm('Are you sure you want to demote this user?')) {
                const data = {
                    ...u,
                    staff: false
                }

                await dispatch(updateUser(data))
                await refreshUserLists(page)
            }
        } else {
            dispatch(setErrorNotification('You do not have permission to demote users'))
        }
    }

    if (loadingAdmins || loadingUsers) {
        return (
            <Container {...AdminManage.loading.props} sx={AdminManage.loading.style}>
                <Loading />
            </Container>
        )
    }

    return (
        <Container sx={AdminManage.container}>
            <Typography {...AdminManage.title} gutterBottom>
                {PAGES.manage_admin.title}
            </Typography>
            <Typography variant='p' gutterBottom>
                Note all staff can view this page but only the superuser can actually promote or demote. If any other staff attempts to promote or demote you will recieve an error.
                Furthermore, please note that if a superuser account needs to be demoted you should speak to the developer team.
            </Typography>
            <Typography {...AdminManage.subheading} sx={AdminManage.subheadingMT} gutterBottom>Current Admin</Typography>
            <TableContainer component={Paper} sx={AdminManage.subheadingMB}>
                <Table sx={AdminManage.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell {...AdminManage.typeAlign}>Email</TableCell>
                            <TableCell {...AdminManage.columnAlign}>Demote</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {admins.map(u => (
                            <TableRow
                                key={`${u.id}`}
                                sx={AdminManage.tableRow}
                            >
                                <TableCell {...AdminManage.tableCell.props} sx={AdminManage.tableCell.style}>
                                    {u.name}
                                </TableCell>
                                <TableCell {...AdminManage.typeAlign}>{u.email}</TableCell>
                                <TableCell {...AdminManage.columnAlign}>
                                    {!u.admin ? <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={() => handleDemote(u)}>
                                        <ArrowDownwardIcon />&nbsp;Demote
                                    </Button> : <Typography variant='p'>Cannot demote superuser</Typography>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography {...AdminManage.subheading} gutterBottom>Search Users</Typography>
            <Typography {...AdminManage.subheading2} gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        sx={{marginBottom: '20px'}}
                        fullWidth
                        value={searchDraft}
                        onChange={e => setSearchDraft(e.target.value)}
                        label="Filter By Name Or Email"
                    />
                </Grid>
            </Grid>
            <TableContainer component={Paper}>
                <Table sx={AdminManage.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell {...AdminManage.typeAlign}>Email</TableCell>
                            <TableCell {...AdminManage.columnAlign}>Promote</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {searchUsers.map(u => (
                            <TableRow
                                key={`${u.id}`}
                                sx={AdminManage.tableRow}
                            >
                                <TableCell {...AdminManage.tableCell.props} sx={AdminManage.tableCell.style}>
                                    {u.name}
                                </TableCell>
                                <TableCell {...AdminManage.typeAlign}>{u.email}</TableCell>
                                <TableCell {...AdminManage.columnAlign}>
                                    <Button {...AdminManage.approve.props} sx={AdminManage.approve.style} onClick={() => handlePromote(u)}>
                                        <ArrowUpwardIcon />&nbsp;Promote
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination sx={AdminManage.pagination} count={totalPages} page={page} onChange={(event, value) => setPage(value)} />
        </Container>
    )
}

export default ManageAdmin
